/**
 * Deterministic report-template derivation, shared by the co-builder (document
 * uploads) and the wizard's "Generate template" button. Turns an uploaded
 * sample document — or a natural-language format description — into a
 * {title, sections} report template (an editable outline). The agent produces
 * the actual report as Markdown at run time; styling is applied by the report
 * theme (report-doc.ts), so no LaTeX is involved.
 */

import {
  DashboardChartKind,
  ReportSection,
  WizardReportConfig,
} from '../services/agent-builder.interface';

export const STARTER_SECTIONS = ['Executive summary', 'Key findings', 'Risks', 'Recommendations'];

export interface DerivedReport {
  title: string;
  sections: ReportSection[];
  format: 'html' | 'pdf' | 'xlsx' | 'pptx';
}

const DERIVABLE_FORMATS: ReadonlySet<string> = new Set(['html', 'pdf', 'xlsx', 'pptx']);

/** Normalize the mixed string|ReportSection[] config shape to typed sections —
 *  a legacy string becomes a text section ({title}); an object passes through.
 *
 *  By default empty sections (no title and no chartKind) are dropped, which is
 *  what derivation/skeleton/submit want. Pass {keepEmpty: true} for the section
 *  EDITOR, which must retain a freshly-added blank row so the user can type into
 *  it — otherwise "Add section" appears to do nothing (the blank is filtered out
 *  on the very next render). */
export function normalizeReportSections(
  sections?: Array<string | ReportSection>,
  opts?: { keepEmpty?: boolean },
): ReportSection[] {
  const normalized = (sections ?? []).map((s) =>
    typeof s === 'string'
      ? { title: s }
      : {
          title: s?.title ?? '',
          chartKind: s?.chartKind,
          description: s?.description,
          columns:
            s?.chartKind === 'table' && Array.isArray(s?.columns)
              ? s.columns.filter((c): c is string => typeof c === 'string' && c.trim().length > 0)
                  .map((c) => c.trim().slice(0, 40))
                  .slice(0, 12)
              : undefined,
        },
  );
  return opts?.keepEmpty
    ? normalized
    : normalized.filter((s) => s.title.trim().length > 0 || s.chartKind);
}

/** Just the section titles (for places that only need headings). */
export function sectionTitles(sections?: Array<string | ReportSection>): string[] {
  return normalizeReportSections(sections).map((s) => s.title);
}

/** Filename → a human-ish title ("q3-deal-review.pdf" → "Q3 Deal Review"). */
export function titleFromFilename(name: string): string {
  const stem = name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
  return stem.replace(/\b\w/g, (c) => c.toUpperCase()) || 'Report';
}

/** Pull heading-like lines from text: markdown ATX headings win; otherwise
 *  short, title-cased, non-sentence lines (HTML headings arrive on their own
 *  lines from file-extract). */
export function extractHeadings(text: string): string[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const md = lines
    .map((l) => l.match(/^#{1,3}\s+(.+?)\s*#*$/)?.[1]?.trim())
    .filter((h): h is string => !!h);
  if (md.length) return md;

  return lines.filter((l) => {
    if (l.length < 3 || l.length > 80) return false;
    if (/[.?!,:;]$/.test(l)) return false; // sentence-like → body, not a heading
    const words = l.split(/\s+/);
    if (words.length > 9) return false;
    return /[A-Z]/.test(l[0]) || words.some((w) => /^[A-Z]/.test(w));
  });
}

/** Build a {title, sections} template from a sample document's text. */
export function deriveReportTemplate(
  doc: { name: string; text: string },
  currentFormat: 'html' | 'pdf' = 'html',
): DerivedReport {
  const headings = extractHeadings(doc.text);
  const title = headings[0] && headings.length > 1 ? headings[0] : titleFromFilename(doc.name);
  const rest = (headings[0] && headings.length > 1 ? headings.slice(1) : headings)
    .map((s) => s.slice(0, 80))
    .filter((s, i, a) => a.indexOf(s) === i)
    .slice(0, 12);
  const titles = rest.length ? rest : [...STARTER_SECTIONS];
  return { title: title.slice(0, 120), sections: titles.map((t) => ({ title: t })), format: currentFormat };
}

/** Canonical report-section names, keyed by the phrases creators tend to use.
 *  Order matters — more specific patterns first. */
const SECTION_KEYWORDS: ReadonlyArray<readonly [RegExp, string]> = [
  [/\bexec(?:utive)?\s+summary\b/i, 'Executive summary'],
  [/\bsummary\b/i, 'Summary'],
  [/\boverview\b/i, 'Overview'],
  [/\bintro(?:duction)?\b/i, 'Introduction'],
  [/\bbackground\b/i, 'Background'],
  [/\bscope\b/i, 'Scope'],
  [/\bmethodolog(?:y|ies)\b|\bmethods?\b|\bapproach\b/i, 'Methodology'],
  [/\bkey\s+findings\b|\bfindings\b/i, 'Key findings'],
  [/\banalysis\b/i, 'Analysis'],
  [/\bresults?\b/i, 'Results'],
  [/\b(?:kpis?|metrics)\b/i, 'Metrics'],
  [/\brisks?\b/i, 'Risks'],
  [/\bissues?\b/i, 'Issues'],
  [/\brecommendations?\b/i, 'Recommendations'],
  [/\bnext\s+steps\b|\baction\s+items?\b/i, 'Next steps'],
  [/\bconclusions?\b/i, 'Conclusion'],
  [/\bappendix\b|\bappendices\b/i, 'Appendix'],
  [/\btimeline\b/i, 'Timeline'],
  [/\bbudget\b/i, 'Budget'],
];

/** Strip filler so a messy clause ("a bulleted list of key findings") becomes a
 *  clean heading ("Key findings"). Falls back to a title-cased phrase. */
function cleanSectionPhrase(raw: string): string {
  let s = raw.trim().replace(/^[\-*••\d.)\s]+/, ''); // bullet / number markers
  // leading filler / connectors, applied repeatedly ("then a ", "and the ")
  s = s.replace(
    /^(?:and|then|also|finally|next|plus|followed\s+by|with|including?|include|add|a|an|the|some|its?|this|that)\b\s*/gi,
    '',
  );
  // descriptor nouns that wrap the real subject
  s = s
    .replace(/\b(?:bulleted\s+|numbered\s+|short\s+|brief\s+|one[-\s]?page\s+)?list\s+of\s+/i, '')
    .replace(/\b(?:section|paragraph|table|chart|graph|brief|page)s?\b/gi, '')
    .replace(/\bfor\s+each\b.*$/i, '')
    .replace(/\bwith\b.*$/i, '')
    .replace(/\b(?:showing|that|which|listing|containing|covering)\b.*$/i, '')
    .replace(/\bat\s+the\s+(?:end|top|start|beginning|bottom)\b/i, '');
  s = s
    .replace(/[.;:,]+$/, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Detect a chart intent in a clause ("revenue as a bar chart", "trend over
 *  time", "pie of segments"). Returns the chart kind, or undefined for prose. */
export function detectChartKind(clause: string): DashboardChartKind | undefined {
  const c = clause.toLowerCase();
  if (/\bpie\b|\bdonut\b/.test(c)) return 'pie';
  if (/\bline\s*(?:chart|graph)\b|\btrend\b|\bover\s+time\b|\btime\s*series\b/.test(c)) return 'line';
  if (/\bbar\s*(?:chart|graph)\b|\bcolumn\s*(?:chart|graph)\b/.test(c)) return 'bar';
  if (/\bkpis?\b|\bheadline\s+(?:number|metric)\b|\bsingle\s+(?:number|metric|value)\b|\bbig\s+number\b/.test(c)) return 'kpi';
  if (/\btable\b|\bgrid\b/.test(c)) return 'table';
  if (/\bchart\b|\bgraph\b|\bplot\b|\bvisuali[sz]e\b|\bbreakdown\b|\bdistribution\b/.test(c)) return 'bar';
  return undefined;
}

/** A styling / formatting request — these add CSS, NOT a section, so they must
 *  go to the AI (which writes report_css) rather than the instant add-section
 *  path. Without this guard "add some padding in the summary section" wrongly
 *  created a section literally titled "some padding in the summary". */
const STYLE_REQUEST_RE =
  /\b(padding|margin|spacing|gap|colou?rs?|font|fonts?|size|sizes|bold|italic|underline|weight|background|bg|border|outline|align|alignment|indent|justify|shadow|css|styl(?:e|ing)|theme|highlight|shade|width|height|line-?height|letter-?spacing|uppercase|lowercase|capitali[sz]e|rounded|radius)\b/i;

/** Other operations bundled with "add ... section" (rename/remove/convert/
 *  reorder, or "make it a chart") are multi-step or ambiguous — route them to
 *  the AI instead of the deterministic add. */
const CONFLICTING_OP_RE =
  /\b(renames?|renamed|removes?|removed|deletes?|deleted|drops?|reorders?|reorder|moves?|moved|converts?|convert|replaces?|replaced|change\s+the|make\s+(?:it|the|that|this)\b|turn\s+(?:it|the|that|this)\b)\b/i;

/** Parse an "add a section" instruction into a section title + optional chart
 *  kind (text when no chart is named). Returns null when the text isn't a clear
 *  add-a-NEW-section request — the caller then routes the instruction to the AI,
 *  which can add a section, style an existing one (report_css), rename, etc.
 *  Powers the report designer's instant, deterministic "add a section" path.
 *  Deliberately CONSERVATIVE: when in doubt, defer to the AI — a wrong instant
 *  action ("dummy section") is worse than a model round-trip.
 *
 *  Examples (instant):
 *   "add a section called Pricing"               → { title: 'Pricing' }
 *   "add a Revenue section as a bar chart"        → { title: 'Revenue', chartKind: 'bar' }
 *   "add a pie chart section called Market Share" → { title: 'Market Share', chartKind: 'pie' }
 *  Examples (deferred to AI → null):
 *   "add some padding in the summary section"     (styling → report_css)
 *   "add a Risks section and make it a pie chart" (add + convert)
 *   "add a note to the summary section"           (edits an existing section) */
export function parseAddSectionIntent(
  text: string,
): { title: string; chartKind?: DashboardChartKind } | null {
  if (!/\badd\b/i.test(text) || !/\bsections?\b/i.test(text)) return null;
  // Styling, multi-op, or "add X to/in the <existing> section" all mean
  // something other than "create a new section" → let the AI handle them.
  if (STYLE_REQUEST_RE.test(text)) return null;
  if (CONFLICTING_OP_RE.test(text)) return null;
  if (/\badd\b[^.?!]*\b(?:to|into|in|inside|under)\s+(?:the\s+)?[^.?!]*\bsections?\b/i.test(text)) return null;
  let title = '';
  const named = text.match(
    /\b(?:called|titled|named|labell?ed)\s+["']?(.+?)["']?(?=\s+(?:as|with|that|which|using|and|to)\b|[.,!?]|$)/i,
  );
  if (named) {
    title = named[1];
  } else {
    const between = text.match(/\badd\s+(?:a\s+|an\s+|another\s+|new\s+)*(.+?)\s+sections?\b/i);
    if (between) title = between[1];
  }
  const chartKind = detectChartKind(text);
  title = title
    .replace(/\b(?:kpis?|bar|line|pie|donut|table|column)\b/gi, '')
    .replace(/\b(?:charts?|graphs?|plots?)\b/gi, '')
    .replace(/\b(?:new|a|an|another)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[-:\s]+|[-:\s]+$/g, '');
  if (!title || title.length > 60) return null;
  title = title.charAt(0).toUpperCase() + title.slice(1);
  return chartKind ? { title, chartKind } : { title };
}

/** Parse plain-English prose into an ordered list of typed sections, tagging a
 *  section as a graph (with kind) when the clause asks for a chart. */
export function parseDescriptionSectionsTyped(description: string): ReportSection[] {
  // 1. Explicit structure wins: markdown headings or list items.
  const headings = extractHeadings(description);
  if (headings.length > 1) return headings.map((h) => taggedSection(h, h));

  // 2. Drop a leading preamble before a colon ("A one-page brief: a, b, c").
  let prose = description.trim();
  const colon = prose.indexOf(':');
  if (colon > 0 && colon < 60) prose = prose.slice(colon + 1);

  // 3. Split into clauses on list/sentence delimiters and connectors.
  const clauses = prose
    .split(/[\n;,]|\bthen\b|\band\s+(?:then\s+)?|\bfollowed\s+by\b|\bplus\b|•|(?<=\.)\s+/i)
    .map((c) => c.trim())
    .filter(Boolean);

  const out: ReportSection[] = [];
  const seen = new Set<string>();
  for (const clause of clauses) {
    // Prefer a canonical name when the clause mentions a known section.
    const hit = SECTION_KEYWORDS.find(([re]) => re.test(clause));
    const name = hit ? hit[1] : cleanSectionPhrase(clause);
    if (!name || name.length < 2 || name.length > 80) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(taggedSection(name, clause));
  }
  return out;
}

/** A section with the chart kind inferred from `source` (the raw clause). */
function taggedSection(title: string, source: string): ReportSection {
  const chartKind = detectChartKind(source);
  return chartKind ? { title, chartKind } : { title };
}

/** Parse plain-English prose into an ordered list of section headings (titles
 *  only; use parseDescriptionSectionsTyped to also get chart kinds). */
export function parseDescriptionSections(description: string): string[] {
  return parseDescriptionSectionsTyped(description).map((s) => s.title);
}

/** Build a template from a natural-language format description (no document). */
export function deriveReportFromDescription(
  description: string,
  config: WizardReportConfig = {},
): DerivedReport {
  const parsed = parseDescriptionSectionsTyped(description);
  const fallback = normalizeReportSections(config.sections);
  const base = parsed.length
    ? parsed
    : fallback.length
      ? fallback
      : STARTER_SECTIONS.map((t) => ({ title: t }) as ReportSection);
  const sections = base
    .map((s) => (s.chartKind ? { title: s.title.slice(0, 80), chartKind: s.chartKind } : { title: s.title.slice(0, 80) }))
    .slice(0, 12);
  const title =
    (config.title || '').trim() ||
    description.trim().split(/[.\n:]/)[0].slice(0, 60).replace(/\b\w/g, (c) => c.toUpperCase()) ||
    'Report';
  // DerivedReport is for Markdown/document reports; a 'dashboard' config
  // (chart-grid format, handled separately) coerces to 'html'.
  const format = config.format && DERIVABLE_FORMATS.has(config.format) ? config.format : 'html';
  return { title: title.slice(0, 120), sections, format: format as DerivedReport['format'] };
}

// ── Inline charts ──────────────────────────────────────────────────────────
// A report may embed a graph by including a fenced ```chart block in its
// Markdown; the renderer (report-charts.ts / the backend's _md_to_html) turns
// it into an inline SVG. The wizard inserts a ready-to-edit scaffold for each
// kind — the agent fills the data with real (aggregate) values at run time.

export type ReportChartKind = 'bar' | 'line' | 'pie' | 'kpi' | 'table';

export const REPORT_CHART_KINDS: ReadonlyArray<{ kind: ReportChartKind; label: string }> = [
  { kind: 'bar', label: 'Bar' },
  { kind: 'line', label: 'Line' },
  { kind: 'pie', label: 'Pie' },
  { kind: 'kpi', label: 'KPI' },
  { kind: 'table', label: 'Table' },
];

const CHART_SCAFFOLDS: Record<ReportChartKind, unknown> = {
  bar: {
    kind: 'bar',
    title: 'Chart title',
    data: { groups: [{ key: 'Category A', count: 12 }, { key: 'Category B', count: 5 }] },
  },
  line: {
    kind: 'line',
    title: 'Trend over time',
    data: { groups: [{ key: 'Jan', count: 3 }, { key: 'Feb', count: 7 }, { key: 'Mar', count: 5 }] },
  },
  pie: {
    kind: 'pie',
    title: 'Breakdown',
    data: { groups: [{ key: 'Segment A', count: 6 }, { key: 'Segment B', count: 4 }] },
  },
  kpi: {
    kind: 'kpi',
    title: 'Headline number',
    data: { value: 0, label: 'units' },
  },
  table: {
    kind: 'table',
    title: 'Details',
    data: { columns: ['Name', 'Value'], rows: [['Example', 1]] },
  },
};

/** A ready-to-edit ```chart block for the given kind, for insertion into the
 *  report Markdown template. The example values show the expected data shape;
 *  the agent replaces them with real totals at run time. For a 'table' kind,
 *  explicit `columns` (named by the creator via the co-builder) replace the
 *  generic Name/Value placeholder so the preview shows the real headers
 *  before the agent has ever run. */
export function chartScaffold(kind: ReportChartKind, columns?: string[]): string {
  const spec = CHART_SCAFFOLDS[kind] ?? CHART_SCAFFOLDS.bar;
  const withColumns =
    kind === 'table' && columns?.length
      ? { ...(spec as { kind: string; title: string; data: unknown }), data: { columns, rows: [columns.map(() => '—')] } }
      : spec;
  return '```chart\n' + JSON.stringify(withColumns, null, 2) + '\n```';
}

/** Append a chart scaffold to a report's Markdown template (seeding the skeleton
 *  first when the template is still empty, so the chart isn't left floating). */
export function appendChartToMarkdown(config: WizardReportConfig, kind: ReportChartKind): string {
  const base = (config.markdown ?? '').trim() || reportSkeletonMarkdown(config).trim();
  return `${base}\n\n${chartScaffold(kind)}\n`;
}

/** Find the line bounds of the `## <title>` section block in `lines`: from its
 *  heading up to (but excluding) the next `#`/`##` heading or end of document.
 *  Title match is trimmed + case-insensitive. Returns null when not found. */
function _sectionBounds(lines: string[], title: string): { start: number; end: number } | null {
  const target = title.trim().toLowerCase();
  if (!target) return null;
  let start = -1;
  for (let i = 0; i < lines.length; i++) {
    const m = /^##\s+(.*\S)\s*$/.exec(lines[i]);
    if (m && m[1].trim().toLowerCase() === target) {
      start = i;
      break;
    }
  }
  if (start === -1) return null;
  let end = lines.length;
  for (let i = start + 1; i < lines.length; i++) {
    if (/^#{1,2}\s+\S/.test(lines[i])) {
      end = i;
      break;
    }
  }
  return { start, end };
}

/** Extract the Markdown block for the `## <title>` section (heading + its body)
 *  from `markdown`, or null when there is no matching heading. */
export function extractMarkdownSection(markdown: string, title: string): string | null {
  const lines = (markdown ?? '').split('\n');
  const b = _sectionBounds(lines, title);
  if (!b) return null;
  return lines.slice(b.start, b.end).join('\n').replace(/\s+$/, '');
}

/** Replace the `## <title>` section block in `markdown` with `newBlock`, leaving
 *  the rest of the document (other sections, the title, and styling) untouched.
 *  Returns `markdown` unchanged when no matching heading is found. Used by the
 *  per-section "Update template" action so a section edit stays within that
 *  section instead of restyling/rewriting the whole report. */
export function replaceMarkdownSection(markdown: string, title: string, newBlock: string): string {
  const lines = (markdown ?? '').split('\n');
  const b = _sectionBounds(lines, title);
  if (!b) return markdown ?? '';
  const before = lines.slice(0, b.start);
  const after = lines.slice(b.end);
  const block = newBlock.replace(/\s+$/, '').split('\n');
  const out = [...before, ...block];
  if (after.length) out.push('', ...after);
  return out.join('\n').replace(/\n{3,}/g, '\n\n');
}

/** Reconcile a report's Markdown template to its current sections, preserving
 *  authored content where it can. For each section, in its current order:
 *   - keep the existing `## <title>` block (and the prose/chart the creator or
 *     AI wrote in it) when the section still exists with the same kind;
 *   - regenerate a fresh skeleton block when the section is new, was renamed,
 *     or switched between text and a chart.
 *  Blocks whose section was removed are dropped. The `# <title>` line comes from
 *  the config. Used to keep the live preview in step with the section list when
 *  the creator adds/removes/reorders/retypes sections WITHOUT the AI designer —
 *  the AI designer owns richer, whole-template rewrites. An empty template (or
 *  empty section list) falls back to a fresh skeleton. */
export function reconcileMarkdownToSections(
  markdown: string | undefined,
  config: WizardReportConfig,
): string {
  const md = (markdown ?? '').trim();
  const sections = normalizeReportSections(config.sections);
  if (!md || !sections.length) return reportSkeletonMarkdown(config);
  const title = (config.title || 'Report').trim();

  // Single-rename preservation: when EXACTLY one section title no longer matches
  // any heading AND exactly one heading is no longer named by the section list,
  // it's an unambiguous rename — carry the renamed section's authored body across
  // under its new heading instead of regenerating an empty skeleton. Only the
  // unambiguous 1:1 case, so prose can never be mis-attributed.
  const mdHeadings = (md.match(/^##\s+(.*\S)\s*$/gm) ?? []).map((h) => h.replace(/^##\s+/, '').trim());
  const titlesLower = new Set(sections.map((s) => s.title.trim().toLowerCase()));
  const orphanHeadings = mdHeadings.filter((h) => !titlesLower.has(h.toLowerCase()));
  const unmatched = sections.filter((s) => !extractMarkdownSection(md, s.title));
  const renamedFrom =
    unmatched.length === 1 && orphanHeadings.length === 1 ? orphanHeadings[0] : null;

  const blocks = sections.map((s) => {
    const heading = `## ${s.title.trim()}`;
    let existing = extractMarkdownSection(md, s.title);
    if (!existing && renamedFrom && s === unmatched[0]) {
      const prior = extractMarkdownSection(md, renamedFrom);
      if (prior) {
        const body = prior.replace(/^##\s+.*(\r?\n)?/, '').replace(/^\s+/, '');
        existing = `${heading}\n\n${body}`.replace(/\s+$/, '');
      }
    }
    if (existing) {
      // Keep the authored block only when its kind still matches the section
      // (a text section's block has no ```chart fence; a chart section's does).
      const hasChart = /```chart/.test(existing);
      if (hasChart === !!s.chartKind) return existing;
    }
    return s.chartKind
      ? `${heading}\n\n${chartScaffold(s.chartKind, s.columns)}`
      : `${heading}\n\n_(${s.title.trim()} content goes here.)_`;
  });
  return `# ${title}\n\n${blocks.join('\n\n')}\n`;
}

/** Render a report template (title + sections) as a Markdown skeleton — the
 *  shape the agent fills in at run time. Used to preview the template in the
 *  builder and as a fallback when an agent returns no report text. */
export function reportSkeletonMarkdown(config: WizardReportConfig): string {
  const title = (config.title || 'Report').trim();
  const normalized = normalizeReportSections(config.sections);
  const sections = normalized.length ? normalized : STARTER_SECTIONS.map((t) => ({ title: t }) as ReportSection);
  const body = sections
    .map((s) => {
      const heading = `## ${s.title.trim()}`;
      // A graph section emits a ```chart scaffold the agent fills with real
      // data; a text section emits a prose placeholder.
      return s.chartKind
        ? `${heading}\n\n${chartScaffold(s.chartKind, s.columns)}`
        : `${heading}\n\n_(${s.title.trim()} content goes here.)_`;
    })
    .join('\n\n');
  return `# ${title}\n\n${body}\n`;
}
